import { useCallback, useState } from 'react';
import { Size, t, type SuggestionRow } from '@bms2/shared';
import { Dropdown } from '../../components/controls';
import { get } from './api';
import { SiteModal } from './SiteModal';

/** One of the account's open sites as a document form lists it. */
export interface SiteChoiceRow {
  id: string;
  label: string;
}

/**
 * 🔒 The Bill To and Ship To pickers on a document form are Dropdowns over the
 * account's sites, and the list ends with the Add New Site row every suggestion
 * list carries (piece 3, decision 0070; piece 22, T185). Product owner,
 * 2026-09-23: *"the spec calls out the bottom of a drop list requires a add
 * button — why is the wrong control being used"*: the first cut drew a plain
 * select with a button beside it, and this is the control the plan names.
 *
 * The row opens the one + Add Site window with the account the form already
 * chose locked in it, and the picker that opened it lands on the new site once
 * the account's sites have been read again.
 *
 * 🔨 The window is mounted only while it is open and keyed to the account, so it
 * never opens on another account's draft (the modal rule in CLAUDE.md).
 */
export function SitePicker({ picker, label, value, onChange, sites, companyId, companyName, apiBaseUrl = '', lockedReason = null, required = false, placeholder, onCreated, data = {} }: {
  /** Which picker this is, which is also the one a created site lands in. */
  picker: 'billTo' | 'shipTo';
  label: string;
  /** The chosen site's id, or none. */
  value: string | null;
  onChange: (siteId: string) => void;
  /** The account's sites as the form lists them. */
  sites: readonly SiteChoiceRow[];
  /** The account the form has chosen; none yet leaves the picker disabled. */
  companyId: string | null;
  companyName: string | null;
  apiBaseUrl?: string;
  /** Why the picker cannot change at all, where it cannot; it is disabled then. */
  lockedReason?: string | null;
  required?: boolean;
  placeholder?: string;
  /** Answers whether the account's sites were read again; only then has the picker taken the new site. */
  onCreated: (siteId: string) => Promise<boolean>;
  /** The form's own data attributes for the picker, so its suites find it. */
  data?: Record<`data-${string}`, string>;
}) {
  const [open, setOpen] = useState(false);
  const [rereadFailed, setRereadFailed] = useState(false);
  const openerId = `site-picker-${picker}`;
  // The list is the account's own sites, filtered as the person types; there is no server search behind it.
  const search = useCallback(async (query: string): Promise<SuggestionRow[]> => {
    const needle = query.trim().toLowerCase();
    return sites.filter((s) => needle === '' || s.label.toLowerCase().includes(needle)).map((s) => ({ id: s.id, line1: s.label }));
  }, [sites]);
  const chosen = sites.find((s) => s.id === value)?.label ?? null;
  return (
    <span data-site-picker={picker} {...data}>
      <Dropdown
        size={Size.Medium}
        label={label}
        entityLabel={t('document.edit.siteEntity')}
        search={search}
        minChars={0}
        value={chosen}
        onSelect={(row) => onChange(row.id)}
        onAddNew={() => { setRereadFailed(false); setOpen(true); }}
        disabled={!companyId || Boolean(lockedReason)}
        required={required}
        {...(placeholder === undefined ? {} : { placeholder })}
      />
      {/* 🔨 A site saved and then not read back is said so, rather than a picker set to a value it does not list (found in review, 2026-09-22). */}
      {rereadFailed ? <span role="alert" data-add-site-failed="true">{t('document.edit.addSiteRereadFailed')}</span> : null}
      {open && companyId ? (
        <SiteModal
          key={companyId}
          open
          companyId={companyId}
          companyName={companyName}
          apiBaseUrl={apiBaseUrl}
          openerId={openerId}
          onCancel={() => setOpen(false)}
          onCreated={(created) => { setOpen(false); void onCreated(created.siteId).then((ok) => setRereadFailed(!ok)); }}
        />
      ) : null}
    </span>
  );
}

/**
 * The account's sites as the form lists them, read again once a site is added.
 * 🔨 The re-read is the company's own sites read (T654), not the form's whole
 * defaults answer: taking that again would reset the terms, the dates and
 * anything else the person had already typed, and cost every lookup behind it.
 */
export function useSiteChoices(answer: { companyId: string; sites: readonly SiteChoiceRow[] } | null, path: string | null, apiBaseUrl: string) {
  const [fresh, setFresh] = useState<{ companyId: string; sites: readonly SiteChoiceRow[] } | null>(null);
  const sites: readonly SiteChoiceRow[] = answer ? (fresh && fresh.companyId === answer.companyId ? fresh.sites : answer.sites) : [];
  const reread = useCallback(async (): Promise<boolean> => {
    if (!path) return false;
    const read = await get<{ companyId: string; sites: SiteChoiceRow[] }>(path, apiBaseUrl);
    if (!read.ok) return false;
    setFresh({ companyId: read.data.companyId, sites: read.data.sites });
    return true;
  }, [path, apiBaseUrl]);
  /** The handler a picker takes: read the sites again, and set the picker only once the new site is listed. */
  const added = useCallback((set: (id: string) => void) => async (siteId: string): Promise<boolean> => {
    const ok = await reread();
    if (ok) set(siteId);
    return ok;
  }, [reread]);
  return { sites, reread, added };
}
